import { useState, useEffect } from 'react';
import { Semester } from '../types/grades';
import { University } from '../types/universities';

export const DEFAULT_SEMESTER: Semester = {
  id: '1',
  name: 'Semester 1',
  courses: [{ 
    code: '', 
    credits: 0, 
    grade: '',
    isRetake: false,
    isSpecialGrade: false
  }],
  gpa: 0
};

export function useUniversityData(university: University) {
  const [storageKey, setStorageKey] = useState(`gpa_calculator_${university.id}_data`);
  const [semesters, setSemesters] = useState<Semester[]>([DEFAULT_SEMESTER]);
  const [isLoading, setIsLoading] = useState(true);

  // Update storage key when university changes
  useEffect(() => {
    const newStorageKey = `gpa_calculator_${university.id}_data`;
    setStorageKey(newStorageKey);
    setIsLoading(true);
  }, [university.id]);

  // Load university-specific data when university changes
  useEffect(() => {
    try {
      setIsLoading(true);
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsedData: Semester[] = JSON.parse(saved);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setSemesters(parsedData);
        } else {
          setSemesters([DEFAULT_SEMESTER]);
        }
      } else {
        setSemesters([DEFAULT_SEMESTER]);
      }
    } catch (e) {
      console.error('Error loading university data:', e);
      setSemesters([DEFAULT_SEMESTER]);
    } finally {
      setIsLoading(false);
    }
  }, [university.id, storageKey]);

  // Save semesters when they change
  useEffect(() => {
    if (!isLoading) { // Only save if not in loading state
      try {
        localStorage.setItem(storageKey, JSON.stringify(semesters));
      } catch (e) {
        console.error('Error saving university data:', e);
      }
    }
  }, [semesters, storageKey, isLoading]);

  const updateSemesters = (newSemesters: Semester[] | ((prev: Semester[]) => Semester[])) => {
    setSemesters(prev => {
      const updated = typeof newSemesters === 'function' ? newSemesters(prev) : newSemesters;
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving university data:', e);
      }
      return updated;
    });
  };

  const resetSemesters = () => {
    setSemesters([DEFAULT_SEMESTER]);
    localStorage.setItem(storageKey, JSON.stringify([DEFAULT_SEMESTER]));
  };

  return {
    semesters,
    setSemesters: updateSemesters,
    resetSemesters,
    isLoading
  };
}